import React from 'react';
import {ActiveFormInfo} from '../../../redux/selectors/form';
import {Forms} from '../../models/forms';

interface IProps {
  children: any;
}

interface IState {
  activeForm?: Forms;
  currentFieldsDirection: FieldsDirection;
}

export enum FieldsDirection {
  Prev = -1,
  None = 0,
  Next = 1,
}

type FocusFieldConfig = {
  form?: ActiveFormInfo;
  direction?: FieldsDirection;
  onFocus: (formName: string, fieldName: string) => void;
};

export interface IActiveFormContext {
  getActiveForm: () => string;
  setActiveForm: (formName?: Forms) => void;
  focusToNextField: (config: FocusFieldConfig) => void;
}

export const Index = React.createContext<IActiveFormContext | undefined>(undefined);

export class ActiveFormProvider extends React.Component<IProps, IState> {
  state = {
    activeForm: undefined,
    currentFieldsDirection: FieldsDirection.None,
  };

  private setActiveForm = (activeForm?: Forms) => {
    this.setState({activeForm});
  };

  private getActiveForm = (): string => {
    return this.state.activeForm || '';
  };

  private focusToNextField = async (config: FocusFieldConfig) => {
    const {direction, form, onFocus} = config;
    if (!form) return;
    const currentFieldsDirection = direction || this.state.currentFieldsDirection;
    this.setState({currentFieldsDirection});
    const maxIndexOfFields = form.fields.length - 1;
    const currentFieldIndex = form.fields.findIndex((field) => field === form.activeField);
    let fieldIndex =
      currentFieldsDirection === FieldsDirection.Next
        ? nextIndex(currentFieldIndex, maxIndexOfFields)
        : prevIndex(currentFieldIndex);
    onFocus(form.activeFormName, form.fields[fieldIndex]);
  };

  render() {
    const {children} = this.props;
    return (
      <Index.Provider
        value={{
          getActiveForm: this.getActiveForm,
          setActiveForm: this.setActiveForm,
          focusToNextField: this.focusToNextField,
        }}>
        {children}
      </Index.Provider>
    );
  }
}

const nextIndex = (i: number, num: number) => {
  return i + 1 > num ? num : i + 1;
};
const prevIndex = (i: number) => {
  return i - 1 < 0 ? 0 : i - 1;
};

export const useActiveForm = () => {
  const current = React.useContext(Index);
  if (!current) {
    throw Error('ActiveForm context is not initialized');
  }
  return current as IActiveFormContext;
};

export const withActiveForm =
  <P extends IActiveFormContext>(Component: React.ComponentType<P>): React.FC<Omit<P, keyof IActiveFormContext>> =>
  (props) =>
    <ActiveFormConsumer>{(contextProps) => <Component {...(props as P)} {...contextProps} />}</ActiveFormConsumer>;

export const ActiveFormConsumer = Index.Consumer;
